<script setup>
import {
	Toggle,
	Tabs,
	Tab,
	Card,
	Label,
	Tooltip,
	Input,
	Spin,
	Hint,
	ColorPicker,
	Textarea,
	Select,
	Button,
	Icon,
	Modal,
	Checkbox,
	Dropdown,
	MediaUploaderButton,
	TimePicker,
} from "@components";
import { ref, reactive, computed, onMounted, onUnmounted } from "vue";
import {
	textColor,
	progress,
	getPrice,
	timeZones,
	sortedDays,
	validationCheck,
	regex,
	toRGB,
} from "@utils";
import CustomSchedule from "./CustomSchedule.vue";

import EditProfile from "./EditProfile.vue";
import EditService from "./EditService.vue";

// Route.
import { useRoute, useRouter } from "vue-router";
const route = useRoute();
const router = useRouter();

const $settings = prebook_admin.settings || {};

// Service Store.
import { useService } from "@store/service";
const $service = useService();

// Staff Store.
import { useStaff } from "@store/staff";
const $staff = useStaff();

import { useStaffItem } from "@store/staffItem";
const $staffItem = useStaffItem();

import moment from "moment";

onUnmounted(() => {
	$staffItem.resetStaff();
});

// Actions.
const save = async (type) => {
	$staffItem.submit(type);
};

const publish = async () => {
	// $staffItem.staff.meta.status = 'publish'
	$staffItem.state.saving = "publish";
	await save("publish");
};

const draft = async () => {
	// $staffItem.staff.meta.status = 'draft'
	$staffItem.state.saving = "draft";
	await save("draft");
};

const archive = async () => {
	let confirm = await Prompt.fire({
		title: `Archive ${String($settings.labels.staff?.singular).toLowerCase()}`,
		message: `Are you sure you want to archive this ${String(
			$settings.labels.staff?.singular
		).toLowerCase()}?`,
		ok: `Yes, archive`,
		cancel: `No, cancel`,
	});
	if (!confirm.ok) return;

	await save("archive");
};

const publishButton = computed(() => {
	if ($staffItem.staff.meta.status !== "publish" || $staffItem.isNew) {
		return $staffItem.state.saving == "publish" ? "Publishing" : "Publish";
	}

	return $staffItem.state.saving == "publish" ? "Updating" : "Update";
});

const draftButton = computed(() => {
	return $staffItem.state.saving == "draft" ? "Saving" : "Save as draft";
});

const object_states = computed(() => {
	let _states = [];

	if (
		$staffItem.staff.meta?.status &&
		$staffItem.staff.meta?.status !== "publish"
	) {
		_states.push(
			String($staffItem.staff.meta?.status).split("")[0].toUpperCase() +
				String($staffItem.staff.meta?.status).slice(1)
		);
	}

	if ($staffItem.staff.meta?.is_private) {
		_states.push("Private");
	}

	return _states;
});

// Get TextColor.
const getTextColor = (color) => {
	return prebook_admin.options.color_sets[color] || "#fff";
};

const isCustomSchedule = ref(false);

const isScheduleEnabled = (value) => {
	return [true, "true", 1, "1", "yes"].includes(value);
};
const toggleSchedule = (day) => {
	$staffItem.staff.working_hours[day].enabled = isScheduleEnabled(
		$staffItem.staff.working_hours[day].enabled
	)
		? false
		: true;

	if ($staffItem.staff.working_hours[day].enabled) {
		$staffItem.staff.working_hours[day].hours =
			$staffItem.staff.working_hours[day].hours || [];
		if ($staffItem.staff.working_hours[day].hours.length === 0) {
			const newSlot = {
				start: "9:00 AM",
				end: "5:00 PM",
			};

			// Previous day.
			// const previousDay = staff.working_hours[Object.keys(staff.working_hours)[Object.keys(staff.working_hours).indexOf(day) - 1]]
			// console.log(previousDay)
			// If previous day is enabled, then use the previous day's last slot as the new slot's start time.
			// if (previousDay && previousDay.enabled) {
			//     const lastSlot = previousDay.hours[previousDay.hours.length - 1]
			//     newSlot.start = lastSlot.start
			//     newSlot.end = lastSlot.end

			// }

			$staffItem.staff.working_hours[day].hours.push(newSlot);
		}
	}
};

// Slot actions.
const removeSlot = (day, index) => {
	$staffItem.staff.working_hours[day].hours.splice(index, 1);

	if ($staffItem.staff.working_hours[day].hours.length === 0) {
		$staffItem.staff.working_hours[day].enabled = false;
	}
};

const addSlot = (day) => {
	// Previous slot of the same day or previous day.
	let previousSlot =
		$staffItem.staff.working_hours[day].hours[
			$staffItem.staff.working_hours[day].hours.length - 1
		];
	if (!previousSlot) {
		const previousDay =
			staff.working_hours[
				Object.keys(staff.working_hours)[
					Object.keys(staff.working_hours).indexOf(day) - 1
				]
			];
		if (previousDay && previousDay.enabled) {
			previousSlot = previousDay.hours[previousDay.hours.length - 1];
		}
	}

	$staffItem.staff.working_hours[day].hours.push({
		start: previousSlot ? previousSlot.end : "9:00 AM",
		end: previousSlot
			? moment(previousSlot.end, "h:mm A").add(1, "hour").format("h:mm A")
			: "5:00 PM",
	});
};

const updateServicePrice = async (service) => {
	let data = {
		...service,
		meta: {
			price: service.price,
		},
	};
	await $service
		.serviceModel(service.service_id)
		.update(data, service.service_id);

	$service.fetchData();
};

// const isSpecialDay = ref(false)
const isHolidayAndDayOff = ref(false);
// const saveDaysOff = async () => {
// $staffItem.staff.days_off = []
// console.log($staffItem.staff.days_off)
// await $staff.updateStaff($staffItem.staff)
// await $settings.saveSettings('days_off', $settings.daysOff)
// Toast.success('Updated days off')
// }
// saveDaysOff()
const showCustomSchedule = ref(false);
const getHolidays = computed(() => {
	if (!$staffItem.staff.days_off) return [];
	let h = [];
	if (
		$staffItem.staff.days_off &&
		Array.isArray($staffItem.staff.days_off) &&
		$staffItem.staff.days_off.length
	) {
		$staffItem.staff.days_off.forEach((holiday, index) => {
			let date = {
				from: {
					fullDate: holiday[0].date,
					day: moment(holiday[0].date, "YYYY-MM-DD").format("dddd"),
					date: moment(holiday[0].date, "YYYY-MM-DD").format("DD"),
					month: moment(holiday[0].date, "YYYY-MM-DD").format("MMMM"),
					year: moment(holiday[0].date, "YYYY-MM-DD").format("YYYY"),
				},
				to: {},
				time: {
					start: moment(holiday[0].from, "HH:mm:ss").format("h:mm A"),
					end: moment(holiday[0].to, "HH:mm:ss").format("h:mm A"),
				},
			};

			if (holiday[1]) {
				date.to = {
					fullDate: holiday[1].date,
					day: moment(holiday[1].date, "YYYY-MM-DD").format("dddd"),
					date: moment(holiday[1].date, "YYYY-MM-DD").format("DD"),
					month: moment(holiday[1].date, "YYYY-MM-DD").format("MMMM"),
					year: moment(holiday[1].date, "YYYY-MM-DD").format("YYYY"),
				};
			}

			h.push(date);
		});
	}
	return h;
});

const holidays = computed(() => getHolidays.value);

const customScheduleForm = ref({
	from: {},
	to: {},
	time: {
		start: {},
		end: {},
	},
});

const generateSettingsHoliday = async (method = null) => {
	if (!$staffItem.staff.days_off) $staffItem.staff.days_off = [];

	if (!customScheduleForm.value.from.fullDate && method !== "delete") return;

	if (router.currentRoute.value.query.index) {
		holidays.value[router.currentRoute.value.query.index] = {
			from: customScheduleForm.value.from,
			to: customScheduleForm.value.to,
			time: customScheduleForm.value.time,
		};
	} else if (!router.currentRoute.value.query.index && method !== "delete") {
		holidays.value.push({
			from: customScheduleForm.value.from,
			to: customScheduleForm.value.to,
			time: customScheduleForm.value.time,
		});
	}

	$staffItem.staff.days_off = holidays.value.map((holiday) => {
		let h = [];

		if (holiday.from.fullDate) {
			h.push({
				date: holiday.from.fullDate,
				from: moment(holiday.time.start, "h:mm A").format("HH:mm:ss"),
				to: moment(holiday.time.end, "h:mm A").format("HH:mm:ss"),
			});
		}

		if (holiday.to.fullDate) {
			h.push({
				date: holiday.to.fullDate,
				from: moment(holiday.time.start, "h:mm A").format("HH:mm:ss"),
				to: moment(holiday.time.end, "h:mm A").format("HH:mm:ss"),
			});
		}

		return h;
	});
};

const addCustomSchedule = async () => {
	await generateSettingsHoliday();

	// await saveDaysOff()

	let { index, ...restParams } = router.currentRoute.value.query;
	router.push({
		query: { ...restParams },
	});

	showCustomSchedule.value = false;
	customScheduleForm.value = {
		from: {},
		to: {},
		time: {
			start: {},
			end: {},
		},
	};
};

const getCustomScheduleInfo = (holiday) => {
	let difference = moment(holiday.to.fullDate, "YYYY-MM-DD").diff(
		moment(holiday.from.fullDate, "YYYY-MM-DD"),
		"days"
	);
	let renderDate = () => {
		let from = moment(holiday.from.fullDate, "YYYY-MM-DD").format("DD MMM");
		let to = moment(holiday.to.fullDate, "YYYY-MM-DD").format("DD MMM");

		if (from == to) {
			return from;
		} else if (from && to == "Invalid date") {
			return from;
		} else {
			return `${from} - ${to}`;
		}
	};

	return {
		difference: difference + 1 || 1,
		renderDate: renderDate(),
	};
};

const editCustomSchedule = async (holiday, index) => {
	showCustomSchedule.value = true;
	router.push({
		query: {
			...router.currentRoute.value.query,
			index: index,
		},
	});
	customScheduleForm.value = holidays.value[index];
};

const deleteCustomSchedule = async (holiday, index) => {
	if (index >= 0 && index < holidays.value.length) {
		holidays.value.splice(index, 1);
	} else {
		return Toast.error("Invalid index provided");
	}

	await generateSettingsHoliday("delete");
	// await saveDaysOff()
};

const closeCustomeSchedule = () => {
	let { index, ...restParams } = router.currentRoute.value.query;
	router.push({
		query: { ...restParams },
	});

	showCustomSchedule.value = false;
};

onMounted(async () => {
	await $staffItem.mountStaffItem();
	$staffItem.isStaffChanged = false
});
const backButton = async () => {
	if ($staffItem.isStaffChanged) {
		const choice = await Prompt.fire({
			title: 'Unsaved Changes Detected',
			message: `You have unsaved changes. Any unsaved changes will be lost. <br> Are you sure you want to continue?`,
			ok: 'Yes, proceed',
			cancel: 'No, cancel',
		});
		if (!choice.ok) return;
	}
	
	if (route.query.dashboard) {
		router.push({ name: "dashboard" });
	} else if (route.query.backTo) {
		router.push(route.query.backTo);
	} else {
		router.push({
			name: "staff.list",
			query: { ...router.currentRoute.value.query },
		});
	}
};

const deleteStaff = async () => {
	const response = await $staffItem.staff.delete();

	if (response.success) {
		Toast.success(`${$settings.labels.staff?.singular} deleted successfully`);
		router.push({
			name: "staff.list",
			query: { ...router.currentRoute.value.query },
		});
	} else {
		// Toast.error(response.message)
	}
};
</script>

<template>
	<!-- <Modal :progress="progress('profile, service, working-hours, days-off')" :loading="$staffItem.state.loading"> -->
	<Modal :progress="progress('profile, service')" :loading="$staffItem.state.loading">
		<!-- header  -->
		<template #header>
			<div class="px-8 py-6 border-b-[#EAECF0] border-b flex items-center justify-between shadow-[0px_3px_0px_0px_#eaecf0]">
				<div @click="backButton" class="cursor-pointer flex items-center gap-5 text-[#101828] text-lg font-semibold">
					<Icon name="arrowNarrowLeft" size="24px"></Icon>Back
				</div>
				<div v-if="!$staffItem.state.loading" class="flex items-center h-10 gap-4">
					<Button size="lg" type="secondary_gray" radius="square" @click.prevent="draft">{{ draftButton }}</Button>
					<Button size="lg" type="primary" radius="square" @click.prevent="publish">{{ publishButton }}</Button>
					<div @click="backButton">
						<Icon name="xClose" size="20px"></Icon>
					</div>
				</div>
				<div v-else class="flex items-center h-10">
					<Spin size="25px" />
				</div>
			</div>
		</template>

		<template #default>
			<div class="md:w-[736px] m-auto" :class="{ 'cursor-wait': $staffItem.state.loading }">
				<!-- page heading -->
				<div class="mt-[29px] mb-6 flex items-center justify-between gap-3 text-[#344054] text-2xl font-semibold leading-8">
					<div class="flex items-center gap-3">
						{{ $staffItem.isNew ? 'Add' : 'Update' }} {{ String($settings.labels.staff?.singular).toLowerCase() }}
						<div class="mt-2 text-xs" v-if="object_states.length">- {{ object_states.join(', ') }}</div>
					</div>
					<div class="flex items-center gap-3">
						<div v-if="$staffItem.state.loading" class="animate-pulse bg-[#F2F4F7] rounded-full w-[115px] h-[24px]"></div>
						<Tooltip v-else :title="`Accessible to admins and ${String($settings.labels.staff?.plural).toLowerCase()}`" :subtitle="`When enabled, this ${String($settings.labels.staff?.singular).toLowerCase()} won't be visible on the booking form for ${String($settings.labels.customer?.plural).toLowerCase()}`" positionX="left">
							<Toggle v-model="$staffItem.staff.meta.is_private">Set Private</Toggle>
						</Tooltip>
					</div>
				</div>

				<!-- form tabs -->
				<Tabs default="profile">
					<Tab id="profile">Profile</Tab>
					<Tab id="service" class="capitalize">{{ $settings.labels.service?.plural }}</Tab>
					<!-- <Tab id="working-hours">Working Hours</Tab> -->
					<!-- <Tab id="days-off">Days Off</Tab> -->

					<!-- profile tab -->
					<template #profile>
						<Card>
							<EditProfile />
						</Card>
						<!-- For Add Page -->
						<div class="flex md:flex-row flex-col items-center justify-between gap-5">
							<div v-if="!$staffItem.isNew" class="flex gap-2 mt-10">
								<Button size="lg" type="tertiary_gray" radius="square" classButton="!w-[113px] justify-between text-[#667085]" @click="deleteStaff()">
									<div class="w-[70%]">Delete</div>
									<template #before>
										<Icon name="trash01" size="20px"></Icon>
									</template>
								</Button>
								<Button size="lg" type="tertiary_gray" radius="square" classButton="!w-[113px] justify-between text-[#667085]" @click="archive()">
									<div class="w-[70%]">Archive</div>
									<template #before>
										<Icon name="archive" size="20px"></Icon>
									</template>
								</Button>
							</div>
							<Button size="xl" type="primary" radius="square" classButton="w-full mt-2 md:mt-10" @click.prevent="$router.push({ query: { ...router.currentRoute.value.query, tab: 'service' } })">Continue</Button>
						</div>
					</template>

					<!-- service tab -->
					<template #service>
						<form @submit.prevent="save">
							<Card cardClass="gap-5">
								<template #header>
									<div class="flex flex-wrap items-center justify-between gap-2 md:flex-nowrap md:gap-0">
										<div class="flex flex-col gap-1">
											<div class="text-[#101828] text-lg font-semibold">Offered {{ String($settings.labels.service?.plural).toLowerCase() }}</div>
											<div class="text-[#667085] text-sm font-normal">Choose the {{ String($settings.labels.service?.plural).toLowerCase() }} that this {{ String($settings.labels.staff?.singular).toLowerCase() }} will offer to {{ String($settings.labels.customer?.plural).toLowerCase() }}</div>
										</div>
										<!-- <Toggle v-model="isCustomPrice">Custom Price</Toggle> -->
									</div>
								</template>

								<EditService />
							</Card>
							<!-- <div class="flex items-center gap-10">
                                <Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] mt-10 justify-between" @click.prevent="router.push({ query: {  ...router.currentRoute.value.query,tab: 'profile' } })">
                                    <div class="w-[70%]">Back</div>
                                    <template #before>
                                        <Icon name="arrowNarrowLeft" size="20px"></Icon>
                                    </template>
                                </Button>
                                <Button submit size="xl" type="primary" radius="square" classButton="w-full mt-10 justify-between" @click.prevent="$router.push({ query: {  ...router.currentRoute.value.query,tab: 'working-hours' } })">
                                    <div class="w-[95%]">Next</div>
                                    <template #after>
                                        <Icon name="arrowNarrowRight" size="20px"></Icon>
                                    </template>
                                </Button>
							</div>-->
							<div class="flex items-center gap-10">
								<Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] mt-10 justify-between" @click.prevent="router.push({ query: { ...router.currentRoute.value.query, tab: 'profile' } })">
									<div class="w-[70%]">Prev</div>
									<template #before>
										<Icon name="arrowNarrowLeft" size="20px"></Icon>
									</template>
								</Button>
								<Button @click.prevent="publish" submit size="xl" type="primary" radius="square" classButton="w-full mt-10 justify-between">
									<div class="w-[95%]">{{ publishButton }}</div>
									<template #after>
										<Icon name="arrowNarrowRight" size="20px"></Icon>
									</template>
								</Button>
							</div>
						</form>
					</template>

					<!-- working hours tab -->
					<!-- <template #working-hours>
                        <form @submit.prevent="save">
                            <Card cardClass="gap-5">
                                <template #header>
                                    <div class="flex flex-wrap items-center justify-between gap-2 md:gap-0 md:flex-nowrap">
                                        <div class="flex flex-col gap-1">
                                            <div class="text-[#101828] text-lg font-semibold">Working Hours</div>
                                            <div class="text-[#667085] text-sm font-normal">Assign staffs to the service and manage commission</div>
                                        </div>
                                        <Toggle v-model="$staffItem.staff.meta.custom_working_hours">Set Custom Schedule</Toggle>
                                    </div>
                                </template>
                                <template #default v-if="$staffItem.staff.meta.custom_working_hours">
                                    <Card cardClass="gap-5 mb-5 !p-4">
					<div class="flex flex-col gap-3">-->
					<!-- working day item -->
					<!-- <div v-for="(schedules, day) in $staffItem.staff.working_hours" :key="index" class="flex-col md:flex-row flex items-start justify-between gap-5 md:gap-10 transition-all pb-3 border-b last:border-b-0 border-b-[#D0D5DD]">
                                                <Toggle @click="toggleSchedule(day)" :checked="isScheduleEnabled(schedules.enabled)" class="w-[220px] mt-3">{{ day }}</Toggle>

                                                <div v-if="!isScheduleEnabled(schedules.enabled)" class="w-[284px] text-[#98A2B3] text-base font-medium h-[41px] flex items-center">Unavailable</div>
                                                <div v-else class="flex flex-col justify-between w-full gap-2 md:flex-row">
                                                    <div class="flex flex-col gap-3 overflow-scroll md:overflow-visible">
                                                        <div v-if="schedules.hours" v-for="(slot, index) in schedules.hours" :key="index" class="flex items-center gap-3 w-[284px]">

                                                            <TimePicker v-model="slot.start" class="w-[110px]" placeholder="From" format="12" />
                                                            -
                                                            <TimePicker v-model="slot.end" class="w-[110px]" placeholder="To" format="12" />

                                                            <Icon @click="removeSlot(day, index)" name="trash01" size="20px" class="text-[#667085] hover:text-[#2b2c30] cursor-pointer" />
                                                        </div>
                                                    </div>
                                                    <Icon @click="addSlot(day)" name="plus" size="20px" class="text-[#667085] hover:text-[#2b2c30] cursor-pointer md:mt-3" />
                                                </div>

					</div>-->
					<!-- </div>
                                    </Card>
                                </template>
					</Card>-->

					<!-- Special day -->
					<!-- <Card cardClass="gap-5 mt-5">
                                <template #header>
                                    <div class="flex items-center justify-between">
                                        <div class="flex flex-col gap-1">
                                            <div class="text-[#101828] text-lg font-semibold">Special day</div>
                                            <div class="text-[#667085] text-sm font-normal">Assign staffs to the service and manage commission</div>
                                        </div>
                                        <Toggle v-model="isSpecialDay"></Toggle>
                                    </div>
                                </template>
                                <template #default v-if="isSpecialDay">
                                    <div class="flex gap-5">
                                        <div class="relative p-[6px_8px_8px_8px] border border-[#D0D5DD] rounded-lg w-[216px] text-center">
                                            <Dropdown contentClass="top-[30px] w-[117px]">
                                                <Icon name="dotsVertical" size="36px" class="text-[#98A2B3] p-2 absolute top-0 left-[-5px] cursor-pointer"></Icon>

                                                <template #content>
                                                    <div class="flex flex-col gap-1">
                                                        <div class=" rounded-md mx-1.5 my-0.5 transition">
                                                            <div @click="showCustomSchedule = true" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                                                <Icon size="16px" name="edit05" />
                                                                <div class="text-[#344054] text-sm font-medium">Edit</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </Dropdown>
                                            <div class="text-[#7F56D9] text-4xl font-semibold leading-normal">24-03</div>
                                            <div class="text-[#667085] text-sm font-normal ">Nov-Dec</div>
                                            <div class="text-[#667085] text-xs font-medium py-2 px-3 mt-6 bg-[#F2F4F7] rounded">8:00 am - 6:00 pm</div>
                                        </div>
                                        <div @click="showCustomSchedule = true" class="cursor-pointer p-[6px_8px_8px_8px] border border-dashed border-[#D0D5DD] rounded-lg w-[216px] flex flex-col items-center justify-center">
                                            <Icon name="plus" size="40px" class="p-1.5 text-[#98A2B3] rounded-full border-2 border-dashed border-[#D0D5DD]"></Icon>
                                            <div class="text-[#667085] text-sm font-normal mt-4">Add day</div>
                                        </div>
                                    </div>
                                </template>
					</Card>-->

					<!-- <Modal class="z-[100]" v-if="showCustomSchedule">
                              
                                <template #header>
                                    <div @click="showCustomSchedule = false" class="px-8 py-6 border-b-[#EAECF0] border-b flex items-center justify-between">
                                        <div class="flex items-center gap-5 text-[#101828] text-lg font-semibold">
                                            <Icon name="arrowNarrowLeft" size="24px"></Icon> Back
                                        </div>
                                    </div>
                                </template>
                                
                                <template #default>
                                    <CustomSchedule />

                                    <div class="w-[736px] m-auto flex items-center gap-10">
                                        <Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] mt-10 justify-between" @click.prevent="showCustomSchedule = false">
                                            <div class="w-[70%]">Cancel</div>
                                            <template #before>
                                                <Icon name="arrowNarrowLeft" size="20px"></Icon>
                                            </template>
                                        </Button>
                                        <Button submit size="xl" type="primary" radius="square" classButton="w-full mt-10 justify-between" @click.prevent="showCustomSchedule = false">
                                            <div class="w-[95%]">Save Schedule</div>
                                        </Button>
                                    </div>
                                </template>
					</Modal>-->

					<!-- <div class="flex items-center gap-10">
                                <Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] mt-10 justify-between" @click.prevent="router.push({ query: {  ...router.currentRoute.value.query,tab: 'service' } })">
                                    <div class="w-[70%]">Back</div>
                                    <template #before>
                                        <Icon name="arrowNarrowLeft" size="20px"></Icon>
                                    </template>
                                </Button>
                                <Button submit size="xl" type="primary" radius="square" classButton="w-full mt-10 justify-between" @click.prevent="router.push({ query: { ...router.currentRoute.value.query, tab: 'days-off' } })">
                                    <div class="w-[95%]">Next</div>
                                    <template #after>
                                        <Icon name="arrowNarrowRight" size="20px"></Icon>
                                    </template>
                                </Button>
                            </div>
                        </form>
					</template>-->

					<!-- days off tab -->
					<!-- <template #days-off>
                        <form @submit.prevent="save">
                            <Card cardClass="gap-5 mt-5">
                                <template #header>
                                    <div class="flex items-center justify-between">
                                        <div class="flex flex-col gap-1">
                                            <div class="text-[#101828] text-lg font-semibold">Holidays & Days Off</div>
                                            <div class="text-[#667085] text-sm font-normal">Assign staffs to the service and manage commission</div>
                                        </div>
                                        <Toggle v-model="$staffItem.staff.meta.custom_days_off"></Toggle>
                                    </div>
                                </template>
                                <template #default v-if="$staffItem.staff.meta.custom_days_off">
                                    <div class="flex flex-col gap-5 md:flex-row md:flex-wrap">
                                        <div v-for="(holiday, index) in holidays" class="relative p-[6px_8px_8px_8px] border border-[#D0D5DD] rounded-lg w-full md:w-[156px] text-center">
                                            <Dropdown contentClass="top-[30px] w-[117px]">
                                                <Icon name="dotsVertical" size="36px" class="text-[#98A2B3] p-2 absolute top-0 left-[-5px] cursor-pointer"></Icon>

                                                <template #content>
                                                    <div class="flex flex-col gap-1">
                                                        <div class=" rounded-md mx-1.5 my-0.5 transition">
                                                            <div @click="editCustomSchedule(holiday, index)" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                                                <Icon size="16px" name="edit05" />
                                                                <div class="text-[#344054] text-sm font-medium">Edit</div>
                                                            </div>
                                                            <div @click="deleteCustomSchedule(holiday, index)" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                                                <Icon size="16px" name="trash01" />
                                                                <div class="text-[#344054] text-sm font-medium">Delete</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </Dropdown>
                                            <div class="text-[#7F56D9] text-4xl font-semibold leading-normal mt-[12px]">{{ getCustomScheduleInfo(holiday).difference }}</div>
                                            <div class="text-[#667085] text-sm font-normal ">{{ getCustomScheduleInfo(holiday).difference > 1 ? 'Days' : 'Day' }}</div>
                                            <div class="text-[#667085] text-xs font-medium py-2 px-3 mt-6 bg-[#F2F4F7] rounded">{{ getCustomScheduleInfo(holiday).renderDate }}</div>
                                        </div>
                                        <div @click="showCustomSchedule = true" class="min-h-[160px] cursor-pointer p-[6px_8px_8px_8px] border border-dashed border-[#D0D5DD] rounded-lg w-full md:w-[156px] flex flex-col items-center justify-center">
                                            <Icon name="plus" size="40px" class="p-1.5 text-[#98A2B3] rounded-full border-2 border-dashed border-[#D0D5DD]"></Icon>
                                            <div class="text-[#667085] text-sm font-normal mt-4">Add day</div>
                                        </div>
                                    </div>
                                </template>
                            </Card>

                            <Modal class="z-[100]" style="position:fixed !important;" v-if="showCustomSchedule">
                                <template #header>
                                    <div @click="closeCustomeSchedule()" class="px-8 py-6 border-b-[#EAECF0] border-b flex items-center justify-between">
                                        <div class="flex items-center gap-5 text-[#101828] text-lg font-semibold">
                                            <Icon name="arrowNarrowLeft" size="24px"></Icon> Back
                                        </div>
                                    </div>
                                </template>
                                <template #default>
                                    <CustomSchedule v-model="customScheduleForm" class="mt-5" />

                                    <div class="md:w-[736px] m-auto flex flex-col md:flex-row items-center gap-2 md:gap-10">
                                        <Button @click.prevent="closeCustomeSchedule()" size="xl" type="secondary_gray" radius="square" classButton="w-full md:!w-[180px] md:mt-10 justify-between">
                                            <div class="w-[70%]">Cancel</div>
                                            <template #before>
                                                <Icon name="arrowNarrowLeft" size="20px"></Icon>
                                            </template>
                                        </Button>
                                        <Button @click.prevent="addCustomSchedule()" :disabled="!customScheduleForm.from.fullDate" submit size="xl" type="primary" radius="square" classButton="w-full md:mt-10 justify-between">
                                            <div class="w-[95%]">Save Schedule</div>
                                        </Button>
                                    </div>
                                </template>
                            </Modal>

                            <div class="flex items-center gap-10">
                                <Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] mt-10 justify-between" @click.prevent="router.push({ query: {  ...router.currentRoute.value.query,tab: 'working-hours' } })">
                                    <div class="w-[70%]">Back</div>
                                    <template #before>
                                        <Icon name="arrowNarrowLeft" size="20px"></Icon>
                                    </template>
                                </Button>
                                <Button @click.prevent="publish" submit size="xl" type="primary" radius="square" classButton="w-full mt-10 justify-between">
                                    <div class="w-[95%]">{{ publishButton }}</div>
                                    <template #after>
                                        <Icon name="arrowNarrowRight" size="20px"></Icon>
                                    </template>
                                </Button>
                            </div>
                        </form>
					</template>-->
				</Tabs>
			</div>
		</template>
	</Modal>
</template>
